import type { NamePath } from 'antd/es/form/interface';
import type { TCredentialingActionItem } from 'types/credentialing';
import { DISCLOSURES } from './constants';

// Action needed 字段级定位（协议见 Lark《Action needed 字段级定位：ctaPayload 结构化协议》）。
// 后端只给 fieldKey + 判别字段（state / refId / questionKey），fieldKey → Settings tab 与表单
// 路径的映射由这份前端字典维护；后端给了不认识的 fieldKey 时调用方退化为「只打开对应 tab」。

export type TActionFocus = {
    fieldKey: string;
    state?: string;
    refId?: number;
    questionKey?: string;
    // additionalW9 条目的判别字段（W-9 无州维度，按 Tax ID 定位；未录入过的 taxId 会合成占位条目）
    taxId?: string;
    // additionalW9 待补录清单（新协议：ctaPayload.optumW9Required 里 MISSING 的条目，
    // tin/entityName 预填进条目卡）；有它时优先于单 taxId
    w9Missing?: { taxId: string; entityName?: string }[];
    // 展示在被定位字段上的报错文案（来自 item.reason）
    reason?: string;
};

type SegKind = {
    // CredentialingInfoModal 的 tab key（向导步骤 CredStepKey 或 'payer-info'）
    tab: string;
    // 数组行：refId = 行 id，运行时在已水合表单值里反查下标
    list?: boolean;
    // DTO 有集合前缀但表单字段挂在根上（malpractice）
    strip?: boolean;
    // 六道披露题：questionKey → DISCLOSURES 固定下标
    disclosures?: boolean;
    // payer-requirements 四组：交给 PayerRequirementsPanel 定位（state 定位行）
    payer?: boolean;
    // 老的单地址 fieldKey：映射到 addressList 的第一条
    legacyAddress?: boolean;
};

const single = (tab: string, keys: string[]): Record<string, SegKind> =>
    Object.fromEntries(keys.map((k) => [k, { tab }]));

// fieldKey 首段 → 所在 tab 与解析方式
const SEG: Record<string, SegKind> = {
    // Basic information
    ...single('identity', [
        'firstName',
        'middleName',
        'lastName',
        'formerName',
        'birthDate',
        'gender',
        'npi',
        'billingNpi',
        'credential',
        'citizenship',
        'workAuthorizationType',
        'email',
        'tel',
        'fax',
        'contactAddress',
        'mailingAddress',
    ]),
    // Licenses
    practiceLicenses: { tab: 'licenses', list: true },
    boardCertifications: { tab: 'licenses', list: true },
    deaLicenses: { tab: 'licenses', list: true },
    identification: { tab: 'licenses' },
    cdsCertificate: { tab: 'licenses' },
    // Practice
    ...single('practice', [
        'practiceName',
        'practicePhone',
        'practiceType',
        'specialtyOffered',
        'careDelivery',
        'ehrSystems',
    ]),
    // 多执业地址：按行 id 定位；后端仍可能发单地址时代的 'practiceAddress.xxx'，退化定位到第一条
    addressList: { tab: 'practice', list: true },
    practiceAddress: { tab: 'practice', legacyAddress: true },
    // Malpractice（表单字段在根上：'malpractice.expireDate' → ['expireDate']）
    malpractice: { tab: 'malpractice', strip: true },
    // Background
    providerExperienceList: { tab: 'experience', list: true },
    providerEducationList: { tab: 'experience', list: true },
    // References
    references: { tab: 'references', list: true },
    // Verification
    ...single('final', [
        'hasCaqhAccount',
        'caqhUsername',
        'caqhPassword',
        'ssn',
        'legalTaxName',
        'taxId',
        'businessEntityType',
        'w9FormUrl',
        'hasFacilityPrivileges',
        'facilityName',
    ]),
    disclosures: { tab: 'final', disclosures: true },
    // Payer requirements
    serviceAddresses: { tab: 'payer-info', payer: true },
    medicare: { tab: 'payer-info', payer: true },
    medicaid: { tab: 'payer-info', payer: true },
    additionalW9: { tab: 'payer-info', payer: true },
    focusAreas: { tab: 'payer-info', payer: true },
};

const segOf = (fieldKey: string): SegKind | undefined => SEG[fieldKey.split('.')[0]];

// 引擎（credentialing-api）开的 Medicare/Medicaid 参与度 ask 只带旧协议的 field（'bh.medicare' /
// 'molina.medicaidId' / 'practice.medicare'…），没有 fieldKey。按末段映射到 payer-requirements 的
// 'medicare' / 'medicaid'，否则 Government program details 不展开、也没有可填的输入框。
const LEGACY_GOV_FIELD = /^(?:bh|molina|practice)\.(medicare|medicaid)(?:CertNum|Id)?$/;
export const legacyGovFieldKey = (field?: string): string | undefined => field?.match(LEGACY_GOV_FIELD)?.[1];

// 从 action item 组装 focus；fieldKey 缺失或不认识 → null（调用方退化为只开 tab）
export const buildActionFocus = (item: TCredentialingActionItem): TActionFocus | null => {
    const p = item.ctaPayload;
    const fieldKey = p?.fieldKey || legacyGovFieldKey(p?.field);
    if (!p || !fieldKey || !segOf(fieldKey)) return null;
    // additionalW9 新协议：optumW9Required 全量清单里只有 MISSING 的需要补录；
    // 列表缺失/为空时退回旧的单 taxId 协议
    const w9Missing =
        fieldKey.split('.')[0] === 'additionalW9' && p.optumW9Required?.length
            ? p.optumW9Required
                  .filter((e) => e.status === 'MISSING' && !!e.tin)
                  .map((e) => ({ taxId: e.tin!, entityName: e.entityName || undefined }))
            : undefined;
    return {
        fieldKey,
        state: p.state,
        refId: p.refId ?? item.refId,
        questionKey: p.questionKey,
        taxId: p.taxId,
        w9Missing: w9Missing?.length ? w9Missing : undefined,
        reason: item.reason,
    };
};

export const focusTab = (focus: TActionFocus): string | undefined => segOf(focus.fieldKey)?.tab;

export const isPayerFocus = (focus: TActionFocus): boolean => !!segOf(focus.fieldKey)?.payer;

// 在已水合的表单值里解析目标 NamePath；解析失败返回 null（退化为只展开 section 编辑态）
export const resolveFocusName = (focus: TActionFocus, values: Record<string, any>): NamePath | null => {
    const [head, ...rest] = focus.fieldKey.split('.');
    const seg = SEG[head];
    if (!seg || seg.payer) return null;
    if (seg.disclosures) {
        const idx = DISCLOSURES.findIndex((d) => d.id === focus.questionKey);
        return idx >= 0 && rest.length ? ['disclosures', idx, ...rest] : null;
    }
    if (seg.list) {
        const rows: any[] = Array.isArray(values[head]) ? values[head] : [];
        const idx = rows.findIndex((r) => r && r.id === focus.refId);
        return idx >= 0 && rest.length ? [head, idx, ...rest] : null;
    }
    if (seg.legacyAddress) return rest.length ? ['addressList', 0, ...rest] : null;
    if (seg.strip) return rest.length ? rest : null;
    return [head, ...rest];
};
